/**
 * LinkedInExpirationWarning Component
 *
 * Displays a warning banner when a LinkedIn token is expiring within 60 days.
 * Shows in the Social Media Settings section to prompt users to reconnect.
 *
 * ## Features
 * - **Accessibility**: Includes ARIA attributes (role="alert", aria-live="polite")
 * - **Visual Design**: Yellow warning background matching application patterns
 * - **Animation**: Smooth 300ms slide-down entrance effect
 * - **Conditional Rendering**: Only displays when LinkedIn token expires within 60 days
 * - **Dynamic Content**: Shows specific number of days until expiration
 *
 * ## Positioning
 * Placed in the Social Media Settings section, above the LinkedIn connection status card
 *
 * ## User Experience
 * - Appears when LinkedIn token expiring in 60 days or less
 * - Clearly communicates action needed (disconnect and reconnect)
 * - Works in conjunction with existing expiration email notifications
 * - Does not interrupt user workflow (polite announcement)
 *
 * ## Message Format
 * "Connection expires in XX days. Please disconnect and re-connect soon."
 */

import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { messageWarning } from '../../../../styles/colors';

/**
 * Props for LinkedInExpirationWarning component
 */
export interface LinkedInExpirationWarningProps {
	/**
	 * Number of days until the LinkedIn token expires
	 *
	 * Expected to be a positive integer between 1 and 60
	 * When this value changes, the component will update the message
	 *
	 * @example 15 renders "Connection expires in 15 days..."
	 * @example 1 renders "Connection expires in 1 day..."
	 */
	daysUntilExpiration: number;

	/**
	 * Optional additional className for styling overrides
	 *
	 * Applied in addition to the default styles, allowing:
	 * - Custom positioning adjustments
	 * - Additional spacing or layout modifications
	 *
	 * Note: The default yellow warning background and core styles
	 * remain intact to maintain consistency with other warning patterns.
	 */
	className?: string;

	/**
	 * Test ID for automated testing
	 *
	 * @default "linkedin-expiration-warning"
	 *
	 * Used in tests to:
	 * - Verify warning appears when token expiring soon
	 * - Verify correct days count displayed
	 * - Test accessibility attributes
	 */
	testId?: string;
}

/**
 * Warning component to notify users of expiring LinkedIn tokens
 *
 * @example Basic usage
 * ```tsx
 * <LinkedInExpirationWarning daysUntilExpiration={15} />
 * ```
 *
 * @example With custom styling
 * ```tsx
 * <LinkedInExpirationWarning
 *   daysUntilExpiration={5}
 *   className={css(customStyles.extraMargin)}
 * />
 * ```
 */
export const LinkedInExpirationWarning: React.FC<LinkedInExpirationWarningProps> = ({
	daysUntilExpiration,
	className,
	testId = 'linkedin-expiration-warning',
}) => {
	// Format the message with proper grammar for singular/plural days
	const dayText = daysUntilExpiration === 1 ? 'day' : 'days';

	return (
		<div
			// ARIA: Identifies this as an alert for screen readers
			// Screen readers will announce this content when it appears
			role='alert'
			// ARIA: Polite announcement - won't interrupt current screen reader activity
			// "polite" is appropriate here as this is a warning, not a critical error
			aria-live='polite'
			// Apply both default styles and optional custom className
			className={className ? `${css(styles.warningMessage)} ${className}` : css(styles.warningMessage)}
			// Test ID for automated testing - allows tests to find this element
			data-testid={testId}
		>
			<span className={css(styles.messageContent)}>
				Connection expires in{' '}
				<strong>
					{daysUntilExpiration} {dayText}
				</strong>
				.
				<br />
				Please disconnect and re-connect soon.
			</span>
		</div>
	);
};

/**
 * Styles for LinkedInExpirationWarning
 *
 * Design matches UnsavedChangesWarning and other warning patterns
 * throughout the application for consistent user experience.
 */
const styles = StyleSheet.create({
	warningMessage: {
		// Visual Design
		background: messageWarning, // Yellow warning color from design system (#FEF7D4)
		fontSize: 14, // Standard body text size
		textAlign: 'center', // Center-align for banner-style warnings
		padding: 11, // Comfortable padding for readability

		// Layout
		position: 'relative', // Allows z-index to work
		zIndex: 1, // Ensures warning appears above other content
		marginTop: 10, // Space between username/button row and warning
		marginBottom: 16, // Space between warning and content below

		// Animation: Smooth slide-down entrance effect
		// When component first renders, it slides down from -10px with fade-in
		// This draws attention without being jarring
		animationName: {
			from: {
				opacity: 0, // Start invisible
				transform: 'translateY(-10px)', // Start 10px above final position
			},
			to: {
				opacity: 1, // End fully visible
				transform: 'translateY(0)', // End at final position
			},
		},
		animationDuration: '300ms', // 0.3 second animation (quick but smooth)
		animationTimingFunction: 'ease-out', // Deceleration curve (natural motion)
		animationFillMode: 'both', // Apply both from/to styles (prevents flicker)

		// Animation respects user's reduced motion preferences automatically
		// via CSS prefers-reduced-motion media query (handled by browser)
	},
	messageContent: {
		display: 'inline-block',
	},
});
